/**
 * @fileoverview Connect provider subscription health and durable webhook events to explicit registration, processing toggles, and replay. `kc-internal-backend/functions/pipedrive_webhooks.py` owns ingress/event identity and recovery. A subscription, received inbox event, queued task, and completed business effect are separate facts; dashboard reads do not repair or replay them.
 */

import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import api from '../../api';
import ToastContext from '../../ToastContext';
import { AppButton, AppSpinner, AppTag } from '../shared';
import AppIcon from '../shared/AppIcon';

const isLocalDevelopment = process.env.NODE_ENV === 'development';

const STATUS_SEVERITIES = {
    processed: 'success',
    success: 'success',
    queued: 'info',
    received: 'info',
    processing: 'info',
    skipped: 'secondary',
    deferred: 'warning',
    error: 'danger',
};

const KNOWN_WEBHOOK_SURFACES = [
    {
        id: 'banking_typeform',
        label: 'Banking Typeform',
        source: 'Typeform',
        path: '/api/banking/typeform_webhook',
        ownership: 'BankingSubmissions',
    },
    {
        id: 'google_calendar',
        label: 'Google Calendar RSVP',
        source: 'Google channel',
        path: '/api/calendar/webhook',
        ownership: 'Calendar event sync',
    },
    {
        id: 'slack_channels',
        label: 'Slack channel lookup',
        source: 'n8n proxy',
        path: 'N8N_SLACK_CHANNELS_WEBHOOK_URL',
        ownership: 'Admin Slack forms',
    },
];



function configSeverity(configured) {
    return configured ? 'success' : 'danger';
}



function statusSeverity(status) {
    return STATUS_SEVERITIES[String(status || '').toLowerCase()] || 'secondary';
}



function formatNumber(value) {
    return new Intl.NumberFormat().format(Number(value || 0));
}



function formatDateTime(value) {
    if (!value) {
        return 'None yet';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return String(value);
    }
    return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}



function eventLabel(event) {
    if (!event) {
        return 'None yet';
    }
    const parts = [event.entity, event.action, event.entity_id].filter(Boolean);
    return parts.join(' / ') || `Event #${event.id}`;
}



function subscriptionId(row) {
    return row.id || row.webhook_id || row.subscription_id;
}



function endpointPath(endpointUrl) {
    if (!endpointUrl) {
        return 'Not configured';
    }
    try {
        const url = new URL(endpointUrl);
        return url.pathname;
    } catch {
        return endpointUrl;
    }
}



function subscriptionStatusSeverity(row) {
    if (row.status === 'configured') {
        return 'success';
    }
    if (row.status === 'missing' || row.status === 'not_configured') {
        return 'danger';
    }
    return 'warning';
}



function EventStatusBreakdown({ byStatus, activeFilter, onFilterChange }) {
    const entries = Object.entries(byStatus || {}).sort(([left], [right]) => left.localeCompare(right));
    if (entries.length === 0) {
        return <div className="pipedrive-webhooks-empty">No ledger statuses found.</div>;
    }

    return (
        <div className="pipedrive-webhooks-filter-row" aria-label="Recent event status filters">
            <button
                type="button"
                className={`pipedrive-webhooks-filter-chip${activeFilter === 'all' ? ' is-active' : ''}`}
                onClick={() => onFilterChange('all')}
            >
                <span>All</span>
            </button>
            {entries.map(([status, count]) => (
                <button
                    key={status}
                    type="button"
                    className={`pipedrive-webhooks-filter-chip${activeFilter === status ? ' is-active' : ''}`}
                    onClick={() => onFilterChange(status)}
                >
                    <span>{status}</span>
                    <strong>{formatNumber(count)}</strong>
                </button>
            ))}
        </div>
    );
}



function PipedriveWebhooks() {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [eventFilter, setEventFilter] = useState('all');
    const showToast = useContext(ToastContext);

    const fetchStatus = useCallback(

    async () => {
        setLoading(true);
        try {
            const response = await api.get('/pipedrive/webhooks/status');
            setStatus(response.data);
        } catch (error) {
            console.error('Error loading Pipedrive webhook status:', error);
            const detail = error.response?.data?.error || 'Failed to load webhook status';
            if (isLocalDevelopment) {
                setStatus({
                    success: false,
                    app_environment: 'development',
                    management_enabled: false,
                    management_disabled_reason: 'Webhook status is unavailable in local development right now.',
                    status_load_error: detail,
                    auth_configured: false,
                    api_configured: false,
                    router_url: null,
                    custom_file_url: null,
                    client_won_url: null,
                    ingress_endpoints: [],
                    required_subscription_coverage: [],
                    required_subscription_summary: {},
                    processing_toggles: [],
                    processing_summary: {},
                    subscriptions: [],
                    recent_events: [],
                    event_summary: { total: 0, last_24h: 0, by_status: {}, by_entity: {}, unresolved_count: 0, pending_count: 0 },
                    failure_count: 0,
                    last_delivery: null,
                    last_processed_event: null,
                    last_error_event: null,
                });
            } else {
                showToast({
                    severity: 'error',
                    summary: 'Error',
                    detail,
                    life: 3500,
                });
            }
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    useEffect(() => {
        fetchStatus();
    }, [fetchStatus]);

    const handleRegister = useCallback(

    async () => {
        setBusy(true);
        try {
            const response = await api.post('/pipedrive/webhooks/register');
            showToast({
                severity: response.data?.success ? 'success' : 'warn',
                summary: response.data?.success ? 'Registered' : 'Partial Setup',
                detail: response.data?.success ? 'Pipedrive webhook subscriptions are up to date.' : 'Some webhook subscriptions could not be created.',
                life: 3500,
            });
            fetchStatus();
        } catch (error) {
            showToast({
                severity: 'error',
                summary: 'Registration Failed',
                detail: error.response?.data?.error || 'Failed to register Pipedrive webhooks',
                life: 4000,
            });
        } finally {
            setBusy(false);
        }
    }, [fetchStatus, showToast]);

    const handleDelete = useCallback(

    async (id) => {
        if (!id) {
            return;
        }
        setBusy(true);
        try {
            await api.delete(`/pipedrive/webhooks/subscriptions/${id}`);
            showToast({
                severity: 'success',
                summary: 'Deleted',
                detail: 'Pipedrive webhook subscription deleted.',
                life: 3000,
            });
            fetchStatus();
        } catch (error) {
            showToast({
                severity: 'error',
                summary: 'Delete Failed',
                detail: error.response?.data?.error || 'Failed to delete webhook subscription',
                life: 4000,
            });
        } finally {
            setBusy(false);
        }
    }, [fetchStatus, showToast]);

    const handleReplay = useCallback(

    async (eventId) => {
        setBusy(true);
        try {
            await api.post(`/pipedrive/webhooks/events/${eventId}/replay`);
            showToast({
                severity: 'info',
                summary: 'Replay Queued',
                detail: `Webhook event #${eventId} was queued for replay.`,
                life: 3000,
            });
            fetchStatus();
        } catch (error) {
            showToast({
                severity: 'error',
                summary: 'Replay Failed',
                detail: error.response?.data?.error || 'Failed to replay webhook event',
                life: 4000,
            });
        } finally {
            setBusy(false);
        }
    }, [fetchStatus, showToast]);

    const handleToggleProcessing = useCallback(

    async (toggle) => {
        setBusy(true);
        try {
            await api.patch(`/pipedrive/webhooks/processing/${toggle.id}/active`, {
                is_active: !toggle.is_active,
            });
            showToast({
                severity: 'success',
                summary: toggle.is_active ? 'Processing Disabled' : 'Processing Enabled',
                detail: `${toggle.label} webhook processing updated.`,
                life: 3000,
            });
            fetchStatus();
        } catch (error) {
            showToast({
                severity: 'error',
                summary: 'Update Failed',
                detail: error.response?.data?.error || 'Failed to update webhook processing',
                life: 4000,
            });
        } finally {
            setBusy(false);
        }
    }, [fetchStatus, showToast]);

    const blockers = useMemo(

    () => {
        const nextBlockers = [];
        if (status?.management_enabled === false && status?.management_disabled_reason) {
            nextBlockers.push(status.management_disabled_reason);
        }
        if (status?.status_load_error) {
            nextBlockers.push(status.status_load_error);
        }
        if (status?.ledger_error) {
            nextBlockers.push(status.ledger_error);
        }
        if (status?.subscription_error) {
            nextBlockers.push(status.subscription_error);
        }
        if (status?.management_enabled !== false) {
            if (!status?.auth_configured) {
                nextBlockers.push('Basic Auth credentials are missing.');
            }
            if (!status?.api_configured) {
                nextBlockers.push('Pipedrive API token is missing.');
            }
            if (!status?.router_url) {
                nextBlockers.push('Public webhook base URL is missing.');
            }
        }
        return nextBlockers;
    }, [status]);

    const subscriptions = status?.subscriptions || [];
    const recentEvents = status?.recent_events || [];
    const processingToggles = status?.processing_toggles || [];
    const processingSummary = status?.processing_summary || {};
    const subscriptionSummary = status?.required_subscription_summary || {};
    const requiredCoverage = status?.required_subscription_coverage || [];
    const eventSummary = status?.event_summary || {};
    const ingressEndpoints = status?.ingress_endpoints?.length ? status.ingress_endpoints : [
        {
            id: 'pipedrive_router',
            label: 'Pipedrive router',
            source: 'router',
            endpoint_url: status?.router_url,
            auth_type: 'Basic Auth',
            configured: Boolean(status?.router_url && status?.auth_configured),
        },
        {
            id: 'pipedrive_custom_file',
            label: 'Pipedrive custom file',
            source: 'custom_file',
            endpoint_url: status?.custom_file_url,
            auth_type: 'Basic Auth',
            configured: Boolean(status?.custom_file_url && status?.auth_configured),
        },
        {
            id: 'client_won_compat',
            label: 'Client Won compatibility',
            source: 'client_won_compat',
            endpoint_url: status?.client_won_url,
            auth_type: 'Compatibility endpoint',
            configured: Boolean(status?.client_won_url),
        },
    ];
    const filteredEvents = eventFilter === 'all' ?
        recentEvents :
        recentEvents.filter((event) => String(event.status || '').toLowerCase() === eventFilter);
    const activeProcessingCount = processingSummary.active ?? processingToggles.filter((toggle) => toggle.is_active).length;
    const coverageConfiguredCount = subscriptionSummary.configured_count ?? 0;
    const coverageRequiredCount = subscriptionSummary.required_count ?? requiredCoverage.length;
    const registerDisabled = busy || blockers.length > 0;

    if (loading && !status) {
        return (
            <div className="pipedrive-webhooks-card">
                <AppSpinner />
                <span>Loading Pipedrive webhook status...</span>
            </div>
        );
    }

    return (
        <div className="pipedrive-webhooks">
            <section className="pipedrive-webhooks-hero">
                <div className="pipedrive-webhooks-hero__copy">
                    <div className="pipedrive-webhooks-card__eyebrow">Webhook Intake</div>
                    <h2 className="pipedrive-webhooks-card__title">Pipedrive Webhooks</h2>
                    <div className="pipedrive-webhooks-hero__meta">
                        <AppTag value={status?.app_environment || 'environment unknown'} severity="secondary" rounded />
                        <AppTag
                            value={status?.management_enabled ? 'subscription management enabled' : 'management disabled'}
                            severity={status?.management_enabled ? 'success' : 'warning'}
                            rounded
                        />
                    </div>
                </div>
                <div className="pipedrive-webhooks-card__actions">
                    <AppButton
                        label="Refresh"
                        icon="pi pi-refresh"
                        onClick={fetchStatus}
                        disabled={busy || loading}
                        variant="secondary"
                    />
                    <AppButton
                        label="Register / Update Required Webhooks"
                        icon="pi pi-bolt"
                        onClick={handleRegister}
                        disabled={registerDisabled}
                    />
                </div>
            </section>

            <div className="pipedrive-webhooks-kpi-grid">
                <div className="pipedrive-webhooks-kpi">
                    <span className="pipedrive-webhooks-kpi__label">Ledger events</span>
                    <strong>{formatNumber(eventSummary.total)}</strong>
                    <span>{formatNumber(eventSummary.last_24h)} in the last 24h</span>
                </div>
                <div className="pipedrive-webhooks-kpi">
                    <span className="pipedrive-webhooks-kpi__label">Unresolved failures</span>
                    <strong>{formatNumber(eventSummary.unresolved_count ?? status?.failure_count)}</strong>
                    <span>{formatNumber(eventSummary.pending_count)} pending or deferred</span>
                </div>
                <div className="pipedrive-webhooks-kpi">
                    <span className="pipedrive-webhooks-kpi__label">Required subscriptions</span>
                    <strong>{coverageConfiguredCount}/{coverageRequiredCount || 0}</strong>
                    <span>{formatNumber(subscriptionSummary.stale_count)} stale endpoint match(es)</span>
                </div>
                <div className="pipedrive-webhooks-kpi">
                    <span className="pipedrive-webhooks-kpi__label">Processing switches</span>
                    <strong>{activeProcessingCount}/{processingSummary.total ?? processingToggles.length}</strong>
                    <span>{formatNumber(processingSummary.persisted)} manually overridden</span>
                </div>
            </div>

            <section className="pipedrive-webhooks-card">
                <div className="pipedrive-webhooks-section-heading">
                    <h3>Configuration</h3>
                    <AppTag value={blockers.length ? `${blockers.length} blocker(s)` : 'ready'} severity={blockers.length ? 'danger' : 'success'} />
                </div>
                <div className="pipedrive-webhooks-status-grid">
                    <div className="pipedrive-webhooks-status">
                        <span className="pipedrive-webhooks-status__label">Basic Auth</span>
                        <AppTag value={status?.auth_configured ? 'configured' : 'missing'} severity={configSeverity(status?.auth_configured)} />
                    </div>
                    <div className="pipedrive-webhooks-status">
                        <span className="pipedrive-webhooks-status__label">Pipedrive API</span>
                        <AppTag value={status?.api_configured ? 'configured' : 'missing'} severity={configSeverity(status?.api_configured)} />
                    </div>
                    <div className="pipedrive-webhooks-status">
                        <span className="pipedrive-webhooks-status__label">Ledger</span>
                        <AppTag value={status?.ledger_error ? 'unavailable' : 'available'} severity={status?.ledger_error ? 'danger' : 'success'} />
                    </div>
                </div>

                {blockers.length > 0 &&
                    <div className="pipedrive-webhooks-blockers">
                        {blockers.map((blocker) => (
                            <div key={blocker} className="pipedrive-webhooks-blocker">
                                <AppIcon icon="pi pi-exclamation-triangle" />
                                <span>{blocker}</span>
                            </div>
                        ))}
                    </div>}

                <div className="pipedrive-webhooks-summary">
                    <div>
                        <span className="pipedrive-webhooks-summary__label">Last delivery</span>
                        <span>{eventLabel(status?.last_delivery)}</span>
                    </div>
                    <div>
                        <span className="pipedrive-webhooks-summary__label">Last processed</span>
                        <span>{eventLabel(status?.last_processed_event)}</span>
                    </div>
                    <div>
                        <span className="pipedrive-webhooks-summary__label">Last error</span>
                        <span>{eventLabel(status?.last_error_event)}</span>
                    </div>
                </div>
            </section>

            <section className="pipedrive-webhooks-card">
                <div className="pipedrive-webhooks-section-heading">
                    <h3>Ingress Map</h3>
                    <AppTag value={`${ingressEndpoints.length} active surface(s)`} severity="info" />
                </div>
                <div className="pipedrive-webhooks-endpoint-grid">
                    {ingressEndpoints.map((endpoint) => (
                        <div key={endpoint.id} className="pipedrive-webhooks-endpoint-card">
                            <div className="pipedrive-webhooks-endpoint-card__header">
                                <div>
                                    <div className="pipedrive-webhooks-row__title">{endpoint.label}</div>
                                    <div className="pipedrive-webhooks-row__meta">{endpoint.source} / {endpoint.auth_type}</div>
                                </div>
                                <AppTag value={endpoint.configured ? 'ready' : 'missing'} severity={endpoint.configured ? 'success' : 'danger'} />
                            </div>
                            <code>{endpoint.endpoint_url || 'Not configured'}</code>
                            {endpoint.processor &&
                                <div className="pipedrive-webhooks-endpoint-card__foot">
                                    <span>{endpoint.ledger}</span>
                                    <span>{endpoint.processor}</span>
                                </div>}
                        </div>
                    ))}
                </div>
            </section>

            <section className="pipedrive-webhooks-card">
                <div className="pipedrive-webhooks-section-heading">
                    <h3>Required Pipedrive Subscriptions</h3>
                    <AppTag
                        value={`${coverageConfiguredCount}/${coverageRequiredCount || 0} configured`}
                        severity={coverageConfiguredCount === coverageRequiredCount && coverageRequiredCount > 0 ? 'success' : 'warning'}
                    />
                </div>
                {requiredCoverage.length === 0 ?
                    <div className="pipedrive-webhooks-empty">No required subscription coverage returned.</div> :
                    <div className="pipedrive-webhooks-coverage-grid">
                        {requiredCoverage.map((row) => (
                            <div key={row.entity} className="pipedrive-webhooks-coverage-row">
                                <div>
                                    <div className="pipedrive-webhooks-row__title">{row.entity} / {row.event_action || '*'}</div>
                                    <div className="pipedrive-webhooks-row__meta">{endpointPath(row.endpoint_url)}</div>
                                </div>
                                <div className="pipedrive-webhooks-row__controls">
                                    {row.stale_count > 0 &&
                                        <AppTag value={`${row.stale_count} stale`} severity="warning" />}
                                    <AppTag value={row.status || 'unknown'} severity={subscriptionStatusSeverity(row)} />
                                </div>
                            </div>
                        ))}
                    </div>}
            </section>

            <section className="pipedrive-webhooks-card">
                <div className="pipedrive-webhooks-section-heading">
                    <h3>Processing</h3>
                    <AppTag
                        value={`${activeProcessingCount}/${processingToggles.length} active`}
                        severity={activeProcessingCount === processingToggles.length ? 'success' : 'warning'}
                    />
                </div>
                {processingToggles.length === 0 ?
                    <div className="pipedrive-webhooks-empty">No webhook processing toggles found.</div> :
                    <div className="pipedrive-webhooks-table pipedrive-webhooks-table--compact">
                        {processingToggles.map((toggle) => (
                            <div key={toggle.id} className="pipedrive-webhooks-row">
                                <div>
                                    <div className="pipedrive-webhooks-row__title">{toggle.label}</div>
                                    <div className="pipedrive-webhooks-row__meta">
                                        {toggle.source} / {toggle.entity} / {toggle.env_var}
                                    </div>
                                </div>
                                <div className="pipedrive-webhooks-row__controls">
                                    <AppTag
                                        value={toggle.is_active ? 'active' : 'disabled'}
                                        severity={toggle.is_active ? 'success' : 'warning'}
                                    />
                                    {toggle.persisted &&
                                        <AppTag value="override" severity="secondary" />}
                                    <AppButton
                                        label={toggle.is_active ? 'Disable' : 'Enable'}
                                        icon={toggle.is_active ? 'pi pi-pause' : 'pi pi-play'}
                                        variant={toggle.is_active ? 'danger' : 'secondary'}
                                        onClick={() => handleToggleProcessing(toggle)}
                                        disabled={busy}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>}
            </section>

            <section className="pipedrive-webhooks-card">
                <div className="pipedrive-webhooks-section-heading">
                    <h3>Observed Subscriptions</h3>
                    <AppTag value={`${subscriptions.length} found`} severity={subscriptions.length ? 'success' : 'warning'} />
                </div>
                {subscriptions.length === 0 ?
                    <div className="pipedrive-webhooks-empty">No Pipedrive webhook subscriptions returned from the API.</div> :
                    <div className="pipedrive-webhooks-table">
                        {subscriptions.map((subscription) => {
                            const id = subscriptionId(subscription);
                            return (
                                <div key={id || JSON.stringify(subscription)} className="pipedrive-webhooks-row">
                                    <div>
                                        <div className="pipedrive-webhooks-row__title">
                                            {subscription.event_object || subscription.object || 'Webhook'}
                                            {' / '}
                                            {subscription.event_action || subscription.action || '*'}
                                        </div>
                                        <div className="pipedrive-webhooks-row__meta">
                                            {subscription.subscription_url || subscription.url || 'No URL shown'}
                                        </div>
                                    </div>
                                    <AppButton
                                        label="Delete"
                                        icon="pi pi-trash"
                                        variant="danger"
                                        onClick={() => handleDelete(id)}
                                        disabled={busy || !id}
                                    />
                                </div>
                            );
                        })}
                    </div>}
            </section>

            <section className="pipedrive-webhooks-card">
                <div className="pipedrive-webhooks-section-heading">
                    <h3>Recent Events</h3>
                    <AppTag value={`${filteredEvents.length}/${recentEvents.length} shown`} severity="info" />
                </div>
                <EventStatusBreakdown
                    byStatus={eventSummary.by_status}
                    activeFilter={eventFilter}
                    onFilterChange={setEventFilter}
                />
                {filteredEvents.length === 0 ?
                    <div className="pipedrive-webhooks-empty">No webhook events match this view.</div> :
                    <div className="pipedrive-webhooks-table">
                        {filteredEvents.map((event) => (
                            <div key={event.id} className="pipedrive-webhooks-row">
                                <div>
                                    <div className="pipedrive-webhooks-row__title">
                                        #{event.id} {eventLabel(event)}
                                    </div>
                                    <div className="pipedrive-webhooks-row__meta">
                                        {formatDateTime(event.received_at)} / task {event.celery_task_id || 'none'} / retry {event.retry_count || 0}
                                    </div>
                                    {event.error_message &&
                                        <div className="pipedrive-webhooks-row__error">{event.error_message}</div>}
                                </div>
                                <div className="pipedrive-webhooks-row__controls">
                                    <AppTag value={event.status || 'unknown'} severity={statusSeverity(event.status)} />
                                    <AppButton
                                        label="Replay"
                                        icon="pi pi-replay"
                                        onClick={() => handleReplay(event.id)}
                                        disabled={busy}
                                        variant="secondary"
                                    />
                                </div>
                            </div>
                        ))}
                    </div>}
            </section>

            <section className="pipedrive-webhooks-card">
                <div className="pipedrive-webhooks-section-heading">
                    <h3>Other Webhook Surfaces</h3>
                    <AppTag value={`${KNOWN_WEBHOOK_SURFACES.length} cataloged`} severity="secondary" />
                </div>
                <div className="pipedrive-webhooks-surface-grid">
                    {KNOWN_WEBHOOK_SURFACES.map((surface) => (
                        <div key={surface.id} className="pipedrive-webhooks-surface">
                            <div className="pipedrive-webhooks-row__title">{surface.label}</div>
                            <div className="pipedrive-webhooks-row__meta">{surface.source} / {surface.ownership}</div>
                            <code>{surface.path}</code>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}

export default PipedriveWebhooks;
